import { ReactNode } from 'react';
import BoardExplorer from '../board/BoardExplorer';
import ProductPanel from '../product/ProductPanel';
import PurchaseOrderPanel from '../product/PurchaseOrderPanel';
import AcceptanceInspectionPanel from '../product/AcceptanceInspectionPanel';
import MidInspectionPanel from '../product/MidInspectionPanel';
import FinalInspectionPanel from '../product/FinalInspectionPanel';
import ProcessChartPanel from '../product/ProcessChartPanel';
import ProductManualPanel from '../product/ProductManualPanel';
import DrawingPanel from '../product/DrawingPanel';
import ProductionDailyReportPanel from '../product/ProductionDailyReportPanel';
import NonconformanceReportPanel from '../product/NonconformanceReportPanel';
import ImprovementSuggestionPanel from '../quality/ImprovementSuggestionPanel';
import ExternalAuditPanel from '../quality/ExternalAuditPanel';
import OfficialDocumentPanel from '../document/OfficialDocumentPanel';
import InternalDraftPanel from '../mypage/InternalDraftPanel';
import MeetingMinutesPanel from '../mypage/MeetingMinutesPanel';
import SafetyPpeIssuancePanel from '../safety/SafetyPpeIssuancePanel';
import SafetyCheckSheetPanel from '../safety/SafetyCheckSheetPanel';
import ContractComments from './ContractComments';
import {
  ContractStage,
  isEmbedStageLink,
  STAGE_DOC_LINK_OPTIONS,
  STAGE_PRODUCT_LINK_OPTIONS,
  STAGE_QUALITY_LINK_OPTIONS,
  STAGE_SAFETY_LINK_OPTIONS,
} from '../../types/contract';
import './ContractStageEditor.css';

interface ContractStageEmbedProps {
  stage: ContractStage;
  /** 계약서 ID. 보드 연동 시 이 계약에 첨부된 파일만 표시/업로드 */
  contractId?: number | null;
}

function embedLabel(linkType: string | null | undefined, fallback: string) {
  return (
    STAGE_PRODUCT_LINK_OPTIONS.find((opt) => opt.linkType === linkType)?.label ||
    STAGE_QUALITY_LINK_OPTIONS.find((opt) => opt.linkType === linkType)?.label ||
    STAGE_DOC_LINK_OPTIONS.find((opt) => opt.linkType === linkType)?.label ||
    STAGE_SAFETY_LINK_OPTIONS.find((opt) => opt.linkType === linkType)?.label ||
    fallback
  );
}

function ContractStageEmbed({ stage, contractId }: ContractStageEmbedProps) {
  const linkType = stage.linkType ?? (stage.boardId != null ? 'BOARD' : null);

  if (linkType === 'BOARD' && stage.boardId != null) {
    if (contractId == null) {
      return (
        <div className="contract-stage-placeholder">
          <strong>{stage.stageName || '단계'} · 문서함</strong>
          <p>
            계약서를 먼저 저장하면 이 단계에 파일을 첨부할 수 있습니다.
            {stage.boardName ? ` (연동: ${stage.boardName})` : ''}
          </p>
        </div>
      );
    }

    return (
      <div className="contract-stage-embed">
        <BoardExplorer
          boardId={stage.boardId}
          boardName={stage.boardName || '문서함'}
          contractId={contractId}
        />
      </div>
    );
  }

  if (isEmbedStageLink(linkType)) {
    const wrap = (node: ReactNode) => (
      <div className="contract-stage-embed contract-stage-embed--product">{node}</div>
    );

    if (linkType === 'PRODUCT_LIST') return wrap(<ProductPanel />);
    if (linkType === 'PRODUCT_ORDER') {
      return wrap(<PurchaseOrderPanel contractId={contractId} embedded />);
    }
    if (linkType === 'PRODUCT_ACCEPTANCE') {
      return wrap(<AcceptanceInspectionPanel contractId={contractId} embedded />);
    }
    if (linkType === 'PRODUCT_MID') {
      return wrap(<MidInspectionPanel contractId={contractId} embedded />);
    }
    if (linkType === 'PRODUCT_FINAL') {
      return wrap(<FinalInspectionPanel contractId={contractId} embedded />);
    }
    if (linkType === 'PRODUCT_PROCESS_CHART') {
      return wrap(<ProcessChartPanel contractId={contractId} embedded />);
    }
    if (linkType === 'PRODUCT_MANUAL') {
      return wrap(<ProductManualPanel contractId={contractId} embedded />);
    }
    if (linkType === 'PRODUCT_DRAWING') {
      return wrap(<DrawingPanel contractId={contractId} embedded />);
    }
    if (linkType === 'PRODUCT_DAILY') {
      return wrap(<ProductionDailyReportPanel contractId={contractId} embedded />);
    }
    if (linkType === 'PRODUCT_NCR') {
      return wrap(<NonconformanceReportPanel contractId={contractId} embedded />);
    }
    if (linkType === 'QUALITY_SUGGESTION') {
      return wrap(<ImprovementSuggestionPanel contractId={contractId} embedded />);
    }
    if (linkType === 'EXTERNAL_AUDIT') {
      return wrap(<ExternalAuditPanel contractId={contractId} embedded />);
    }
    if (linkType === 'OFFICIAL_DOCUMENT') {
      return wrap(<OfficialDocumentPanel contractId={contractId} embedded />);
    }
    if (linkType === 'INTERNAL_DRAFT') {
      return wrap(<InternalDraftPanel contractId={contractId} embedded />);
    }
    if (linkType === 'MEETING_MINUTES') {
      return wrap(<MeetingMinutesPanel contractId={contractId} embedded />);
    }
    if (linkType === 'SAFETY_PPE') {
      return wrap(<SafetyPpeIssuancePanel contractId={contractId} embedded />);
    }
    if (linkType === 'SAFETY_CHECK') {
      return wrap(<SafetyCheckSheetPanel contractId={contractId} embedded />);
    }

    return (
      <div className="contract-stage-placeholder">
        <strong>{embedLabel(linkType, stage.stageName)}</strong>
        <p>연동 화면을 불러오지 못했습니다.</p>
      </div>
    );
  }

  return (
    <div className="contract-stage-embed contract-stage-embed--notes">
      <ContractComments
        contractId={contractId}
        stageId={stage.stageId ?? null}
        enabled={contractId != null && stage.stageId != null}
        title={`${stage.stageName || '단계'} · 메모`}
        subtitle={
          stage.stageId == null
            ? '계약서를 저장하면 이 단계에 메모와 첨부파일을 남길 수 있습니다.'
            : '이 단계의 진행 메모와 첨부파일을 남길 수 있습니다.'
        }
      />
    </div>
  );
}

export default ContractStageEmbed;
